import { type HTMLMotionProps, motion } from "motion/react";
import { Skeleton } from "@/components/ui/skeleton";
import type { PaymentMethodKey } from "@/lib/schemas/paycode/payment-methods";
import { cn } from "@/lib/utils";

type InstructionContainerProps = HTMLMotionProps<"div"> & {
  isError?: boolean;
};

export const InstructionContainer = ({
  className,
  children,
  isError = false,
  ...props
}: InstructionContainerProps) => (
  <motion.div
    layout="position"
    transition={{ type: "spring", duration: 0.16, mass: 0.2 }}
    className={cn("space-y-8", isError && "opacity-40", className)}
    {...props}
  >
    {children}
  </motion.div>
);

const CardSkeleton = () => <Skeleton className="h-[60px] w-full rounded-lg" />;

const StepLines = ({ widths }: { widths: string[] }) => (
  <div className="space-y-3">
    {widths.map((width, i) => (
      <Skeleton key={`sk-line-${i}-${width}`} className={cn("h-5", width)} />
    ))}
  </div>
);

export const InstructionsSkeleton = ({
  method,
}: {
  method: Exclude<PaymentMethodKey, "card">;
}) => (
  <div className="space-y-8">
    {method === "codi" ? (
      <div className="space-y-2">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-3/4" />
      </div>
    ) : (
      <Skeleton className="h-5 w-full" />
    )}

    {method === "spei" && (
      <div className="space-y-4">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    )}

    {method === "cie" && (
      <div className="space-y-3">
        <StepLines widths={["w-full", "w-5/6", "w-full", "w-4/6"]} />
        <CardSkeleton />
        <div className="flex w-full flex-row items-center justify-center gap-2">
          <Skeleton className="h-[38px] w-[88px]" />
          <Skeleton className="h-[38px] w-[88px]" />
          <Skeleton className="h-[38px] w-[88px]" />
          <Skeleton className="h-[38px] w-[88px]" />
        </div>
        <CardSkeleton />
        <CardSkeleton />
      </div>
    )}

    {(method === "codi" || method === "tpvBarcode") && (
      <div className="flex w-full flex-col items-center space-y-1">
        {method === "codi" && <Skeleton className="h-5 w-[200px] rounded-lg" />}
        <Skeleton className="h-60 w-60 rounded-lg" />
      </div>
    )}

    {method === "cash" && (
      <>
        <div className="flex w-full flex-col items-center space-y-1">
          <Skeleton className="h-5 w-[200px] rounded-lg" />
          <Skeleton className="h-36 w-[272px] rounded-lg" />
        </div>
        <StepLines widths={["w-full", "w-5/6", "w-full", "w-3/4"]} />
      </>
    )}

    <Skeleton className="h-4 w-3/4" />
  </div>
);
